import { Button } from "@circleco/compass/components/Button";
import { Icon } from "@circleco/compass/components/Icon";
import { Menu } from "@circleco/compass/components/Menu";
import type { MenuOptions } from "@circleco/compass/components/Menu";
import { Select } from "@circleco/compass/components/Select";
import { useRef, useState } from "react";

import { t } from "@/i18n-js/instance";
import {
  entityIcons,
  MasqueradeEntityIcon,
} from "@/react/components/MasqueradeBanner/MasqueradeEntityIcon";
import {
  entityKey,
  entityOption,
  useSelectedEntityOption,
  withCurrentEntity,
} from "@/react/components/MasqueradeBanner/pickerHelpers";
import type { MasqueradePickerProps } from "@/react/components/MasqueradeBanner/types";
import { classNames } from "@/react/helpers/twMergeWithCN";

import styles from "./picker.module.css";

const categories = [
  { type: "CommunityMember", key: "members" },
  { type: "Paywall", key: "paywalls" },
  { type: "InvitationLink", key: "links" },
  { type: "AccessGroup", key: "access_groups" },
] as const;

export const MasqueradeEntityPicker = ({
  currentEntity,
  isError,
  isLoading,
  isSwitching,
  onOpenChange,
  onQueryChange,
  onRetry,
  onSelect,
  open,
  query,
  records,
}: MasqueradePickerProps) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [hasInputChanged, setHasInputChanged] = useState(false);
  const hasQuery = query.trim().length > 0;
  const isSearching = hasQuery || (open && hasInputChanged);
  const currentKey = currentEntity ? entityKey(currentEntity) : undefined;
  const browseRecords = withCurrentEntity(records, currentEntity);
  const selectedOption = useSelectedEntityOption(currentEntity);
  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) setHasInputChanged(false);
    onOpenChange(isOpen);
  };

  const menuOptions: MenuOptions = isError
    ? [
        {
          label: t("view_only_masquerading.picker.retry"),
          icon: "refresh",
          onClick: onRetry,
          closeOnClick: false,
        },
      ]
    : isLoading
      ? [{ label: t("view_only_masquerading.picker.loading"), disabled: true }]
      : categories.map(category => {
          const entities = browseRecords.filter(
            entity => entity.type === category.type,
          );
          return {
            label: t(`view_only_masquerading.categories.${category.key}`),
            icon: entityIcons[category.type],
            submenu:
              entities.length > 0
                ? entities.map(entity => ({
                    label: entity.name,
                    prefix: <MasqueradeEntityIcon entity={entity} />,
                    suffix:
                      entityKey(entity) === currentKey ? (
                        <Icon
                          name="checkmark"
                          size="sm"
                          aria-label={t(
                            "view_only_masquerading.picker.current",
                          )}
                        />
                      ) : undefined,
                    onClick: () => onSelect(entity),
                  }))
                : [
                    {
                      label: t("view_only_masquerading.picker.no_entities"),
                      disabled: true,
                    },
                  ],
          };
        });

  return (
    <div
      id="masquerade-entity-picker"
      ref={wrapperRef}
      data-browsing={open && !isSearching}
      className={classNames(
        "relative min-w-0 w-[266px] max-w-full",
        styles.picker,
      )}
    >
      <Select
        searchable
        icon="magnifying-glass"
        initialMessage=""
        aria-label={t("view_only_masquerading.banner.title_not_selected")}
        placeholder={t("view_only_masquerading.picker.placeholder")}
        value={selectedOption}
        inputValue={isSearching ? query : (currentEntity?.name ?? "")}
        options={
          hasQuery && !isLoading && !isError ? records.map(entityOption) : []
        }
        filter={null}
        isItemEqualToValue={(option, value) => option.value === value.value}
        disabled={isSwitching}
        startContent={
          !isSearching && currentEntity ? (
            <MasqueradeEntityIcon entity={currentEntity} />
          ) : undefined
        }
        isLoading={isLoading}
        emptyMessage={
          isError
            ? t("view_only_masquerading.picker.load_error")
            : t("view_only_masquerading.picker.no_matches")
        }
        i18n={{ loadingMessage: t("view_only_masquerading.picker.loading") }}
        open={open && hasQuery}
        align="start"
        dropdownMaxWidth={335}
        onOpenChange={(nextOpen, details) => {
          if (details.reason !== "input-change") handleOpenChange(nextOpen);
        }}
        onInputValueChange={(value, details) => {
          if (
            details.reason === "input-change" ||
            details.reason === "clear-press"
          ) {
            const inputEvent = details.event;
            const nextQuery =
              !isSearching && inputEvent instanceof InputEvent
                ? (inputEvent.data ??
                  inputEvent.dataTransfer?.getData("text/plain") ??
                  "")
                : value;
            setHasInputChanged(true);
            onQueryChange(nextQuery);
            handleOpenChange(true);
          }
        }}
        onValueChange={option => {
          const entity = records.find(
            record => entityKey(record) === option?.value,
          );
          if (entity) onSelect(entity);
        }}
      />
      {/* Menu owns positioning and focus; its inert anchor shares the Select's bounds. */}
      <div
        className="pointer-events-none absolute inset-0 opacity-0"
        aria-hidden
      >
        <Menu
          trigger={
            <Button
              fullWidth
              disabled={isSwitching}
              tabIndex={-1}
              onFocus={() =>
                wrapperRef.current
                  ?.querySelector<HTMLInputElement>("input[role=combobox]")
                  ?.focus()
              }
            >
              {t("view_only_masquerading.picker.browse_options")}
            </Button>
          }
          disabled={isSwitching}
          open={open && !hasQuery && !isSwitching}
          align="start"
          dropdownMaxWidth={190}
          options={menuOptions}
          onOpenChange={nextOpen => {
            if (!hasQuery) handleOpenChange(nextOpen);
          }}
        />
      </div>
    </div>
  );
};
